import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useAppToast } from "@/src/hooks/useAppToast";
import { useTranslations } from "@/src/i18n/i18n";
import { invalidateApiPaths } from "@/src/libs/invalidateApiPaths";
import { ROUTES } from "@/src/routes/paths";

/**
 * Les trois gestes d'administration d'un événement.
 *
 * ## Ils ne disent pas la même chose, et l'écran ne doit pas les confondre
 *
 * - **fermer** pose le statut `close`, l'état *final* d'un événement : la
 *   partie est réputée jouée. L'événement **reste visible** de tous. Fermer
 *   pour cacher mentirait à ses inscrits sur ce qui lui est arrivé ;
 * - **désapprouver** le rend invisible de tout le monde sauf des
 *   administrateurs, sans rien détruire, et se défait ;
 * - **supprimer** est irréversible et emporte le fil de discussion.
 *
 * Les deux premières invalident la liste **et** la fiche. La troisième n'a plus
 * de fiche à recharger — l'écran navigue vers la liste, sinon il afficherait un
 * événement disparu puis basculerait sur son propre écran « introuvable », ce
 * qui se lit comme une panne alors que c'est le résultat demandé.
 */
export function useEventActions(eventId: number) {
  const $api = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const t = useTranslations("moderation");
  const { showSuccess, showApiError } = useAppToast();

  // Les deux chemins : une fermeture change la pastille de statut dans la liste
  // **et** l'état affiché sur la fiche. La route renvoie certes la fiche remise
  // à jour, mais la réécrire par `setQueryData` exigerait de recomposer à
  // l'identique la clé d'`useEvent` — un `init` qui dériverait laisserait
  // l'écran muet sans que rien n'échoue. Cf. `invalidateApiPaths`.
  const rafraichir = () =>
    invalidateApiPaths(queryClient, [
      "/api/v1/moderation/events",
      "/api/v1/moderation/events/{id}",
    ]);

  const close = $api.useMutation(
    "post",
    "/api/v1/moderation/events/{id}/close",
    {
      onSuccess: () => {
        void rafraichir();
        showSuccess(t("event-closed"));
      },
      onError: (error) => showApiError(t("event-close-error"), error),
    },
  );

  const approval = $api.useMutation(
    "patch",
    "/api/v1/moderation/events/{id}/approval",
    {
      onSuccess: (response) => {
        void rafraichir();
        showSuccess(
          response.data?.unapprovedAt
            ? t("event-unapproved")
            : t("event-approved"),
        );
      },
      onError: (error) => showApiError(t("event-approval-error"), error),
    },
  );

  const remove = $api.useMutation("delete", "/api/v1/moderation/events/{id}", {
    onSuccess: () => {
      void rafraichir();
      showSuccess(t("event-deleted"));
      // `replace` : le bouton « précédent » ne doit pas ramener sur la fiche
      // d'un événement supprimé.
      void navigate(ROUTES.events, { replace: true });
    },
    onError: (error) => showApiError(t("event-delete-error"), error),
  });

  return {
    close: () => close.mutate({ params: { path: { id: eventId } } }),
    isClosing: close.isPending,
    setApproved: (approved: boolean, reason?: string) =>
      approval.mutate({
        params: { path: { id: eventId } },
        // Le motif n'accompagne qu'une désapprobation, et seulement s'il a été
        // saisi : le serveur borne à `min(1)` et l'ignore de toute façon quand
        // on lève la décision.
        body: {
          approved,
          ...(!approved && reason?.trim() ? { reason: reason.trim() } : {}),
        },
      }),
    isSettingApproval: approval.isPending,
    remove: () => remove.mutate({ params: { path: { id: eventId } } }),
    isRemoving: remove.isPending,
  };
}
